import { StepKey } from '@lg/shared';
import type { D365Client } from '../client.js';

/**
 * Live slice (build update 2.2, section 14): provisioning steps named in
 * D365_LIVE_STEPS talk to the real sandbox; every other step stays simulated.
 * Live writes are mirrored into the mock so the simulated steps that follow
 * (copy, people package) still find the company and users.
 */

type Method = keyof D365Client;

/** Client methods each provisioning step needs, when that step runs live. */
export const LIVE_METHODS_BY_STEP: Record<StepKey, readonly Method[]> = {
  preflight: [],
  // The work calendar the journal workflow's due dates use is created with the company.
  createLegalEntity: [
    'isLegalEntityIdAvailable',
    'getLegalEntity',
    'createLegalEntity',
    'ensureWorkCalendar',
  ],
  addressAndRegistration: ['setLegalEntityAddress'],
  copyFromTemplate: [],
  venueOverrides: ['applyOverride'],
  // Every user, worker and employment and the user ↔ worker links, plus the venue's GL accountant
  // position (reporting to the finance director) held by the first person on the roles sheet, live; the package still goes to the (mock) recurring job,
  // which mirrors them into the mock for the read-backs.
  importPeople: [
    'searchUsers',
    'getUser',
    'createUser',
    'ensurePayCycle',
    'findWorkerForUser',
    'ensureJob',
    'ensureWorker',
    'ensurePosition',
    'ensureReportingLine',
    'ensureAssignment',
    'ensurePersonUser',
  ],
  assignSecurity: ['searchUsers', 'getUser', 'createUser', 'assignUserRole', 'listUserRoleAssignments'],
  // Customers, vendors and projects are created record by record over OData. Only the creates go
  // live: lists stay on the mock (which mirrors every live create), so the intake vendor picker and
  // the file checks keep reading the template, and a re-run skips what was already created.
  importCustomersVendors: ['createCustomer', 'createVendor'],
  importProjects: ['createProjectContract', 'createProject'],
  policiesAndParameters: [],
  importWorkflows: [],
  validate: [],
  notify: [],
};

/** Live writes that are also applied to the mock, so simulated steps can continue. */
const MIRRORED: ReadonlySet<Method> = new Set<Method>([
  'createLegalEntity',
  'ensureWorkCalendar',
  'setLegalEntityAddress',
  'applyOverride',
  'createUser',
  'assignUserRole',
  'createCustomer',
  'createVendor',
  'createProjectContract',
  'createProject',
]);

/**
 * D365_LIVE_STEPS: a comma list of steps, or "none" (everything simulated). "none" exists because
 * GitHub won't save an empty variable, and an unset environment variable falls back to the
 * repository's (the RSM sandbox's live steps).
 */
export function parseLiveSteps(value: string | undefined): StepKey[] {
  if (!value?.trim() || value.trim().toLowerCase() === 'none') return [];
  return value.split(',').map((s) => {
    const parsed = StepKey.safeParse(s.trim());
    if (!parsed.success)
      throw new Error(
        `D365_LIVE_STEPS: "${s.trim()}" is not a provisioning step (${StepKey.options.join(', ')})`,
      );
    return parsed.data;
  });
}

export interface HybridOptions {
  live: D365Client;
  mock: D365Client;
  liveSteps: readonly StepKey[];
  /** Mirror failures are logged, never thrown: the mock is a shadow. */
  onMirrorError?: (method: string, err: unknown) => void;
}

export function liveMethods(liveSteps: readonly StepKey[]): ReadonlySet<Method> {
  return new Set(liveSteps.flatMap((s) => LIVE_METHODS_BY_STEP[s]));
}

export function createHybridD365Client(options: HybridOptions): D365Client {
  const live = liveMethods(options.liveSteps);
  return new Proxy(options.mock, {
    get(target, prop, receiver) {
      const name = prop as Method;
      if (!live.has(name)) return Reflect.get(target, prop, receiver);
      return async (...args: unknown[]) => {
        const liveFn = options.live[name] as (...a: unknown[]) => Promise<unknown>;
        const result = await liveFn.apply(options.live, args);
        if (MIRRORED.has(name)) {
          const mockFn = options.mock[name] as (...a: unknown[]) => Promise<unknown>;
          await mockFn
            .apply(options.mock, args)
            .catch((err: unknown) => options.onMirrorError?.(String(name), err));
        }
        return result;
      };
    },
  });
}
